import { useState, useCallback, useEffect, useMemo } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import styled from "styled-components";
import { Download, X } from "lucide-react";
import Table from "../../components/table/Table";
import useAmendments from "../../hooks/useAmendments";
import useAmendmentsExport from "../../hooks/useAmendmentsExport";
import useSelectedAmendmentsExport from "../../hooks/useSelectedAmendmentsExport";
import usePersistedSelection from "../../hooks/usePersistedSelection";
import FilterText from "../../components/filters/FilterText";
import ContactCell from "../../components/table/ContactCell";
import formatDateToFrench from "../../utils/formatDateToFrench";
import Category from "../../components/text/Category";
import Loader from "../../components/interaction/Loader";
import PageNavigator from "../../components/nav/PageNavigator";
import Modules from "../../components/containers/Modules";
import Button from "../../components/cta/Button";
import ExportModal from "../../components/nav/ExportModal";
import { Filter } from "../../types/Filters";
import { Amendment } from "../../types/Amendments";
import { usePersistedFilters } from "../../hooks/filters/usePersistedFilters";
import { exportAmendmentsToCSV } from "../../utils/csvExport";

interface AmdtsExplorerProps {
  textId?: string;
  fileId?: string;
  deputyId?: string;
  userId: string;
  signalId?: string;
  amendmentsTextFollowed?: boolean;
  amendmentsTextNotFollowed?: boolean;
}

const Module = styled.div<{ flexGrow?: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: ${({ flexGrow }) => flexGrow || 1} 0 0;
`;

const Filters = styled.div`
  border-radius: 20px;
  border: 1px solid var(--Main-stroke-1, #ebebeb);
`;

const Header = styled.div`
  display: flex;
  height: 64px;
  padding: 0px 28px;
  align-items: center;
  align-self: stretch;
  border-radius: 20px 20px 0px 0px;
  border-bottom: 1px solid var(--Main-stroke-1, #ebebeb);
  background: #fcfcfc;
`;

const Title = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 0 0;
  color: #3c3c3d;
  font-family: "Sharp Sans";
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
`;

const LinkAction = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 0 0;
  color: #ff734f;
  font-family: "Sharp Sans";
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  text-decoration: none;
`;

const Params = styled.div`
  display: flex;
  padding: 0px 28px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const FilterContainer = styled.div`
  display: flex;
  padding: 10px 0px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-bottom: 1px solid var(--Main-stroke-1, #ebebeb);
`;

const Choice = styled.div`
  display: flex;
  padding: 20px 28px;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  border-radius: 0px 0px 20px 20px;
  border: 1px solid var(--Main-stroke-1, #ebebeb);
`;

const ChoiceText = styled.button`
  color: #828283;
  font-family: "Sharp Sans";
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px; /* 100% */
  border: none;
  background: none;
  cursor: pointer;
`;

const ExportContainer = styled.div`
  display: flex;
  padding: 20px 0;
  justify-content: center;
  width: 100%;
`;

const ExportButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const KeywordFilterBar = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 12px;
  border-radius: 20px;
  background: #fff;
`;

const KeywordInputContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  align-self: stretch;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid var(--Main-stroke-1, #ebebeb);
  background: #fcfcfc;
  min-height: 48px;
  box-sizing: border-box;
`;

const KeywordTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: #fff7f2;
  color: #ff734f;
  font-family: "Sharp Sans";
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`;

const KeywordRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #ff734f;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const KeywordInput = styled.input`
  flex: 1;
  min-width: 150px;
  border: none;
  background: transparent;
  font-family: "Sharp Sans";
  font-size: 12px;
  color: #3c3c3d;
  outline: none;

  &::placeholder {
    color: #828283;
  }
`;

const KeywordLabel = styled.label`
  color: #3c3c3d;
  font-family: "Sharp Sans";
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
`;

const AmdtsExplorer = ({
  textId,
  fileId,
  deputyId,
  userId,
  signalId,
  amendmentsTextFollowed,
  amendmentsTextNotFollowed,
}: AmdtsExplorerProps) => {
  const filterKey = deputyId ? deputyId : textId;
  const [filters, setFilters] = usePersistedFilters(filterKey || "");
  const [pageSelected, setPageSelected] = useState(1);
  const [pageList, setPageList] = useState<number[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [keywordsFilterBar, setKeywordsFilterBar] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState("");

  // Use persistent selection
  const {
    selectedItems: selectedAmendments,
    updateSelection,
    selectedCount,
  } = usePersistedSelection(`amendments-selection-${filterKey || "default"}`);

  const { data, loading, error } = useAmendments(
    fileId,
    textId,
    pageSelected,
    userId,
    filters,
    deputyId,
    signalId,
    amendmentsTextFollowed,
    amendmentsTextNotFollowed,
    false,
    keywordsFilterBar
  );

  const {
    exportAmendments,
    loading: exportLoading,
    error: exportError,
  } = useAmendmentsExport();

  const {
    exportSelectedAmendments,
    loading: exportSelectionLoading,
    error: exportSelectionError,
  } = useSelectedAmendmentsExport();

  useEffect(() => {
    console.log("pageSelected", pageSelected);
  }, [pageSelected]);

  const handleFilterChange = useCallback(
    (id: number, newFilters: Partial<Filter>) => {
      setFilters((prevFilters) =>
        prevFilters.map((filter) =>
          filter.id === id ? { ...filter, ...newFilters } : filter
        )
      );
    },
    [setFilters]
  );

  const addFilter = useCallback(() => {
    setFilters((prevFilters) => [
      ...prevFilters,
      {
        id: prevFilters.length,
        metric: [],
        symbol: [],
        value: [],
      },
    ]);
  }, [setFilters]);

  const removeFilter = useCallback(
    (id: number) => {
      setFilters((prevFilters) =>
        prevFilters.filter((filter) => filter.id !== id)
      );
    },
    [setFilters]
  );

  const handleExportAll = useCallback(async () => {
    try {
      const exportParams = {
        fileId,
        textId,
        userId,
        filters,
        deputyId,
        signalId,
        amendmentsTextFollowed,
        amendmentsTextNotFollowed,
        keywordsFilterBar,
      };

      const amendments = await exportAmendments(exportParams);
      exportAmendmentsToCSV(amendments);
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
    }
  }, [
    fileId,
    textId,
    userId,
    filters,
    deputyId,
    signalId,
    amendmentsTextFollowed,
    amendmentsTextNotFollowed,
    keywordsFilterBar,
    exportAmendments,
  ]);

  const handleExportSelection = useCallback(async () => {
    if (selectedCount === 0) {
      alert("Aucun amendement sélectionné");
      return;
    }

    try {
      const selectedIds = Array.from(selectedAmendments);
      const amendments = await exportSelectedAmendments(selectedIds);
      exportAmendmentsToCSV(amendments);
      setShowExportModal(false);
    } catch (error) {
      console.error("Export selection failed:", error);
    }
  }, [selectedAmendments, selectedCount, exportSelectedAmendments]);

  const handleShowExportModal = useCallback(() => {
    setShowExportModal(true);
  }, []);

  const handleAddKeyword = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && keywordInput.trim()) {
        e.preventDefault();
        if (!keywordsFilterBar.includes(keywordInput.trim())) {
          setKeywordsFilterBar((prev) => [...prev, keywordInput.trim()]);
        }
        setKeywordInput("");
      }
    },
    [keywordInput, keywordsFilterBar]
  );

  const handleRemoveKeyword = useCallback((keyword: string) => {
    setKeywordsFilterBar((prev) => prev.filter((k) => k !== keyword));
  }, []);

  useEffect(() => {
    if (textId && filters.length === 0) {
      setFilters([
        {
          id: 0,
          metric: [],
          symbol: [],
          value: [],
        },
      ]);
    }
  }, [textId, filters.length, setFilters]);

  const sanitizedFilters = useMemo(() => filters, [filters]);

  useEffect(() => {
    if (data) {
      setPageList(Array.from({ length: data.total_pages }, (_, i) => i + 1));
    }
  }, [data]);

  const renderFilters = useMemo(
    () =>
      filters.map((filter) => (
        <FilterContainer key={filter.id}>
          <FilterText
            id={filter.id}
            onRemove={() => removeFilter(filter.id)}
            onFilterChange={handleFilterChange}
            textId={textId || ""}
            filterId={deputyId || ""}
            initialFilter={filter}
          />
        </FilterContainer>
      )),
    [filters, handleFilterChange, removeFilter, textId, deputyId]
  );

  const columns = [
    "N",
    "Parlementaire",
    "Groupe",
    "Date",
    "Emplacement",
    "Sort",
    "Texte",
  ];

  const baseRenderers = {
    N: (value: any, row: any) => <Title>{row.IdOrder}</Title>,
    Groupe: (value: any, row: any) => <Title>{row.GroupName}</Title>,
    Date: (value: any, row: any) => formatDateToFrench(row.CreatedAt),
    Emplacement: (value: any, row: any) => <Title>{row.Spot}</Title>,
    Sort: (value: any, row: any) => <Category>{row.Sort}</Category>,
    Texte: (value: any, row: any) => (
      <LinkAction to={`/file/${row.FileId}/overview`}>
        {row.FileName}
      </LinkAction>
    ),
    Parlementaire: (value: any, row: any) => (
      <ContactCell
        deputy={row.DeputyId}
        name={`${row.FirstName} ${row.LastName}`}
      />
    ),
  };

  const customRenderers = {
    ...baseRenderers,
  };

  return (
    <>
      <KeywordFilterBar>
        <KeywordInputContainer>
          {keywordsFilterBar.map((keyword) => (
            <KeywordTag key={keyword}>
              {keyword}
              <KeywordRemoveButton
                onClick={() => handleRemoveKeyword(keyword)}
                aria-label={`Supprimer ${keyword}`}
              >
                <X size={14} />
              </KeywordRemoveButton>
            </KeywordTag>
          ))}
          <KeywordInput
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={handleAddKeyword}
            placeholder="Tapez un mot-clé et appuyez sur Entrée"
          />
        </KeywordInputContainer>
      </KeywordFilterBar>
      <Modules>
        <Module flexGrow={1}>
          <Filters>
            <Header>
              <Title>Filtres</Title>
            </Header>
            <Params>{renderFilters}</Params>
            <Choice>
              <ChoiceText onClick={addFilter}>+ Filtre</ChoiceText>
            </Choice>
          </Filters>
          <ExportContainer>
            <ExportButton
              typeButton="secondary"
              onClick={handleShowExportModal}
            >
              <Download size={16} />
              Exporter
            </ExportButton>
          </ExportContainer>
        </Module>
        <Module flexGrow={6}>
          {loading && !data ? (
            <Loader />
          ) : (
            data && (
              <Table
                columns={columns}
                data={data.Amendments}
                actionLink={`AmendmentId`}
                customRenderers={customRenderers}
                isLoading={loading}
                emptyDateMessage="Aucun amendement trouvé."
                columnWidths={[
                  "1%",
                  "10%",
                  "3%",
                  "5%",
                  "8%",
                  "10%",
                  "15%",
                  "1%",
                  "1%",
                ]}
                tooltipField="Summary"
                selectable={true}
                selectedItems={selectedAmendments}
                onSelectionChange={updateSelection}
                selectionKey="AmendmentId"
              />
            )
          )}
        </Module>
      </Modules>
      <PageNavigator
        pageSelected={pageSelected}
        pageList={pageList}
        setPageSelected={setPageSelected}
      />
      {showExportModal && (
        <ExportModal
          onClose={() => setShowExportModal(false)}
          onExportAll={handleExportAll}
          onExportSelection={handleExportSelection}
          isLoadingAll={exportLoading}
          isLoadingSelection={exportSelectionLoading}
          selectedCount={selectedCount}
        />
      )}
    </>
  );
};

export default AmdtsExplorer;
